import { IconButton } from '@creditornot/cb-button';
import { MoreHorizontal } from '@creditornot/cb-icons';
import { MenuActionItem } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import React, { useState, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Item, Category } from 'apis/restaurant-api/merchant-admin/menu2/types';

import { MenuPermissionsContext } from '../../../../components/MenuPermissionsContext';
import { WOLT_SPECIALS_CATEGORY_ID } from '../../../constants';

type Props = {
  category: Category | null;
  item: Item;
  onCopy?: (itemId: string, isSpecial: boolean) => void;
  onDelete: () => void;
  onDisable: () => void;
  onEnable: () => void;
  onRemoveFromCategory: () => void;
};

const ToggleButton = styled(IconButton).attrs({ variant: 'brand', size: 'tiny' })``;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 200px;
  padding: 8px;
`;

const Action = styled(MenuActionItem)`
  & + & {
    margin-block-start: 8px;
  }
`;

const ItemActions = ({ category, item, onCopy, onEnable, onDelete, onDisable, onRemoveFromCategory }: Props) => {
  const menuPermissions = useContext(MenuPermissionsContext);

  const [show, setShow] = useState(false);

  const handleItemClick = (action: () => void) => () => {
    action();
    setShow(false);
  };

  return (
    <div onClick={e => e.stopPropagation()}>
      <Popover
        content={
          <PopoverContent>
            {menuPermissions.contentEdit && (
              <Action variant="black" onClick={handleItemClick(() => onCopy?.(item.id, false))}>
                <FormattedMessage id="menu-item.duplicate" />
              </Action>
            )}

            {menuPermissions.contentEdit &&
              menuPermissions.woltSpecials &&
              (!category || category.id !== WOLT_SPECIALS_CATEGORY_ID) && (
                <Action variant="black" onClick={handleItemClick(() => onCopy?.(item.id, true))}>
                  <FormattedMessage id="menu-item.createSpecial" />
                </Action>
              )}

            {item.enabled ? (
              <Action variant="red" onClick={handleItemClick(onDisable)}>
                <FormattedMessage id="menu-item.disable" />
              </Action>
            ) : (
              <Action variant="blue" onClick={handleItemClick(onEnable)}>
                <FormattedMessage id="menu-item.enable" />
              </Action>
            )}

            {menuPermissions.contentEdit && (
              <Action variant="red" onClick={handleItemClick(onDelete)}>
                <FormattedMessage id="menu-item.deleteItem" />
              </Action>
            )}

            {
              // Don't allow users to move items out of the Wolt Specials category, because:
              // * We don't have a use case for that yet.
              // * Some fields are not intended to be used with regular items and we hide them from regular items.
              //   Allowing user to move items out of Wolt Specials would also require resetting those fields.
            }
            {category && category.id !== WOLT_SPECIALS_CATEGORY_ID && (
              <Action variant="red" onClick={onRemoveFromCategory}>
                <FormattedMessage id="menu-item.removeCategory" />
              </Action>
            )}
          </PopoverContent>
        }
        placement="bottom-end"
        open={show}
        onOpenChange={setShow}
        offset={4}
        renderArrow={false}
      >
        {({ refProps }) => (
          <ToggleButton
            tabIndex={-1}
            {...refProps}
            onClick={e => {
              e.preventDefault();
              setShow(!show);
              refProps.onClick?.(e);
            }}
          >
            <MoreHorizontal />
          </ToggleButton>
        )}
      </Popover>
    </div>
  );
};

export default React.memo(ItemActions);
